import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { getTokenExpiry } from "../utils/jwt";
import { isProtectedPath } from "../config/protectedRoutes";
import SessionExpiredModal from "./SessionExpiredModal";

const CHECK_INTERVAL_MS = 30 * 1000;
// 활성 사용자 보호: access 토큰이 만료된 직후~다음 요청이 토큰을 갱신하기 전의
// 짧은 공백에 잘리지 않도록, 최근 조작이 있으면 만료로 판정하지 않는다.
const ACTIVE_GRACE_MS = 30 * 1000;
const ACTIVITY_KEY = "last_activity";
const WRITE_THROTTLE_MS = 5 * 1000;
// mousemove 제외 — 단순 움직임(실제 조작 아님)이 grace를 연장하면 안 된다.
const ACTIVITY_EVENTS = ["mousedown", "keydown", "scroll", "touchstart"];

function clearTokens() {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem(ACTIVITY_KEY);
}

// 세션 만료를 감지한다.
// - 보호 페이지: access 토큰(엄격) 기준. 만료되면 페이지 위에 재로그인 모달을 띄운다.
//   토큰은 지우되 ['user'] 캐시는 유지 → PrivateRoute가 튕겨내지 않고 페이지가 유지된다.
// - 공개 페이지: refresh 토큰(느슨, 1일) 기준. 만료되면 조용히 로그아웃(캐시 clear로
//   헤더만 로그아웃 상태) — 강제 이동 없음.
// reactive: 인터셉터의 auth:expired(refresh 실패)도 현재 페이지 유형에 맞게 처리한다.
export default function SessionWatcher() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const location = useLocation();
    const [reauth, setReauth] = useState(false);
    // 핸들러(interval/이벤트)는 렌더 밖에서 돌아 최신 경로가 필요하므로 ref로 추적.
    const pathRef = useRef(location.pathname);
    pathRef.current = location.pathname;
    const handlingRef = useRef(false);
    const lastWriteRef = useRef(0);

    useEffect(() => {
        const markActivity = () => {
            handlingRef.current = false;
            const now = Date.now();
            if (now - lastWriteRef.current < WRITE_THROTTLE_MS) return;
            lastWriteRef.current = now;
            localStorage.setItem(ACTIVITY_KEY, String(now));
        };

        // 보호 페이지: 토큰만 비우고 캐시는 유지 + 모달 표시.
        const expireOnProtected = () => {
            if (handlingRef.current) return;
            handlingRef.current = true;
            clearTokens();
            setReauth(true);
        };

        // 공개 페이지: 조용히 로그아웃(캐시까지 clear → 헤더 갱신).
        const logoutSilently = () => {
            if (handlingRef.current) return;
            handlingRef.current = true;
            clearTokens();
            queryClient.setQueryData(["user"], null);
        };

        const handleExpiredEvent = () => {
            if (isProtectedPath(pathRef.current)) expireOnProtected();
            else logoutSilently();
        };

        const checkExpiry = () => {
            const access = localStorage.getItem("access_token");
            if (!access) return; // 미로그인
            if (isProtectedPath(pathRef.current)) {
                // 보호 페이지: access 토큰 기준.
                const exp = getTokenExpiry(access);
                if (exp === null) return;
                if (exp * 1000 > Date.now()) return; // 아직 유효
                const last = Number(localStorage.getItem(ACTIVITY_KEY));
                if (last && Date.now() - last < ACTIVE_GRACE_MS) return; // 활성 보호
                expireOnProtected();
            } else {
                // 공개 페이지: refresh 토큰 기준(access는 만료돼도 유지).
                const refresh = localStorage.getItem("refresh_token");
                const exp = refresh ? getTokenExpiry(refresh) : null;
                if (exp !== null && exp * 1000 > Date.now()) return; // 아직 유효
                logoutSilently();
            }
        };

        ACTIVITY_EVENTS.forEach((e) =>
            window.addEventListener(e, markActivity, { passive: true })
        );
        window.addEventListener("auth:expired", handleExpiredEvent);
        window.addEventListener("focus", checkExpiry);
        const intervalId = setInterval(checkExpiry, CHECK_INTERVAL_MS);

        return () => {
            ACTIVITY_EVENTS.forEach((e) =>
                window.removeEventListener(e, markActivity)
            );
            window.removeEventListener("auth:expired", handleExpiredEvent);
            window.removeEventListener("focus", checkExpiry);
            clearInterval(intervalId);
        };
    }, [queryClient]);

    const handleLogin = () => {
        queryClient.setQueryData(["user"], null);
        setReauth(false);
        handlingRef.current = false;
        navigate("/login", { state: { sessionExpired: true } });
    };

    return reauth ? <SessionExpiredModal onLogin={handleLogin} /> : null;
}
